'use client';

import { useState, useEffect } from 'react';
import { Layout, Button, Dropdown, Modal, Input,  Tooltip  } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import {
  SearchOutlined,
  FileOutlined,
  AppstoreOutlined,
  CloseOutlined,
  PlusOutlined,
  MinusOutlined,
  EllipsisOutlined,
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  DownOutlined,
  TeamOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useEmbeddedAuth } from '../../contexts/EmbeddedAuthContext';
import menuService from '../../services/menuService';
import ExploreIcon from '../icons/ExploreIcon';
import TaskIcon from '../icons/TaskIcon';
import UserInfoArea from './UserInfoArea';
import { getToken, getCurrentUser } from '../../services/authService';
import workspaceService from '../../services/workspaceService';
import {
  getMenuData,
  getSavedViewData,
  saveMenuData,
  registerMenuChangeListener,
  unregisterMenuChangeListener,
  forceRefreshMenuData,
} from '../../utils/menuManager';
import {
  removeRecentItem as removeRecentFromStorage,
  RECENTLY_OPENED_UPDATED_EVENT,
} from '../../utils/recentlyOpened';
import WorkspaceAuthModal from '../modals/WorkspaceAuthModal';
import { Popover, Avatar } from 'antd';
import useWorkspacePopoverStyles from '../../styles/components/sidebar/WorkspacePopover';
import { getWorkspaceName, getDefaultTeamId } from '../../utils/workspaceHelper';
import logo from '../../assets/logo.svg';
import adImage from '../../styles/images/advertising.png';
import useViewportWidth from '../../hooks/useViewportWidth';

const { Sider } = Layout;

const AppSidebar = ({ variant = 'sider' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showLoginModal } = useEmbeddedAuth();
  const viewportWidth = useViewportWidth();
  const isCollapsed = variant === 'sider' ? viewportWidth < 1440 : false;
  const siderWidth = isCollapsed ? 72 : 168;
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWorkspace, setCurrentWorkspace] = useState(() => {
    try {
      // 优先从初始化数据中获取 workspace 信息
      const initDataStr = localStorage.getItem('syntrust_init_data');
      if (initDataStr) {
        const initData = JSON.parse(initDataStr);
        if (initData.userDetail?.workspace) {
          return initData.userDetail.workspace;
        }
      }
      
      // 如果初始化数据中没有，尝试从用户数据中获取
      const userStr = localStorage.getItem('syntrust_user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        return userObj.workspace || null;
      }
    } catch {}
    return null;
  });
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  // 添加手动展开/折叠状态
  const [expandedItems, setExpandedItems] = useState({});
  // 当前激活的菜单项ID
  const [activeItemId, setActiveItemId] = useState(null);
  // 当前激活的子菜单项ID
  const [activeSubItemId, setActiveSubItemId] = useState(null);
  // 重命名相关状态
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [currentRenameItem, setCurrentRenameItem] = useState(null);
  const [newMenuName, setNewMenuName] = useState('');

  // 删除相关状态
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // 搜索相关状态
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // 最近打开相关状态
  const [recentlyOpenedItems, setRecentlyOpenedItems] = useState([]);
  const [recentlyOpenedExpanded, setRecentlyOpenedExpanded] = useState(true);

  // 工作区认证状态Modal的显示状态
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showWorkspacePop, setShowWorkspacePop] = useState(false);

  // 获取用户和workspace信息
  const userStr = localStorage.getItem('syntrust_user');
  let userObj = null;
  let userEmail = '';
  let userAvatar = '';
  
  // 从 syntrust_user 获取基本信息
  if (userStr) {
    try {
      userObj = JSON.parse(userStr);
      userEmail = userObj.email || '';
      userAvatar = userObj.avatar || '';
    } catch {}
  }
  
  // 使用工具函数获取 workspace 名称
  const workspaceName = getWorkspaceName();

  // 模拟成员数据（实际应从后端获取团队成员列表）
  const mockMembers = userObj
    ? [
        {
          id: userObj.id,
          name: userObj.name || '未命名',
          avatar: userObj.avatar,
          isLeader: true,
        },
      ]
    : [];

  const { styles } = useWorkspacePopoverStyles();

  // 加载菜单数据的函数
  const loadMenuData = async () => {
    try {
      setLoading(true);
      // 先从本地存储加载自定义菜单
      const customMenuData = getMenuData();
      if (customMenuData) {
        setMenuItems(customMenuData);

        // 初始化展开状态 - 有子菜单的项默认不展开
        const initialExpandState = {};
        customMenuData.forEach((item) => {
          if (item.children && item.children.length > 0) {
            initialExpandState[item.id] = true;
          }
        });
        setExpandedItems(initialExpandState);

        // 根据当前路径设置初始激活状态
        initializeActiveState(customMenuData);
      } else {
        // 如果没有自定义菜单，则从API加载默认菜单
        const data = await menuService.getMenuItems();
        setMenuItems(data);

        // 根据当前路径设置初始激活状态
        initializeActiveState(data);
      }
    } catch (error) {
      console.error('获取菜单数据失败:', error);

      // 出错时尝试从API加载默认菜单
      try {
        const data = await menuService.getMenuItems();
        setMenuItems(data);

        // 根据当前路径设置初始激活状态
        initializeActiveState(data);
      } catch (err) {
        console.error('无法加载默认菜单:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // 加载最近打开的项目
  const loadRecentlyOpenedItems = () => {
    try {
      const dataStr = localStorage.getItem('recently_opened');
      if (dataStr) {
        const data = JSON.parse(dataStr);
        setRecentlyOpenedItems(Array.isArray(data) ? data : []);
      } else {
        setRecentlyOpenedItems([]);
      }

    } catch (e) {
      console.error('加载最近打开的项目失败:', e);
      setRecentlyOpenedItems([]);
    }
  };

  // 初始加载时获取菜单数据
  useEffect(() => {
    loadMenuData();
    loadRecentlyOpenedItems(); // 用本地存储初始化最近打开列表

    // 在控制台暴露强制刷新函数，供调试使用
    window.forceRefreshMenu = () => {

      const refreshedData = forceRefreshMenuData();
      if (refreshedData) {
        setMenuItems(refreshedData);
  
      }
    };

    return () => {
      // 清理全局函数
      delete window.forceRefreshMenu;
    };
  }, []);

  // 单独处理localStorage中的激活提案菜单标记
  useEffect(() => {
    // 检查是否需要激活提案菜单（从localStorage中）
    const shouldActivateTasksMenu = localStorage.getItem('activate_tasks_menu');
    if (shouldActivateTasksMenu === 'true' && menuItems.length > 0) {
      // 查找提案菜单项
      menuItems.forEach((item) => {
        if (item.path === '/tasks') {
          // 设置激活状态
          setActiveItemId(item.id);
          setActiveSubItemId(null); // 清除子菜单激活状态
        }
      });

      // 使用后删除此标记
      localStorage.removeItem('activate_tasks_menu');
    }
  }, [menuItems]);

  // 添加菜单变更监听器
  useEffect(() => {
    // 监听菜单变化的处理函数
    const handleMenuChange = () => {
      loadMenuData();
    };

    // 注册菜单变化监听器
    registerMenuChangeListener(handleMenuChange);

    // 组件卸载时移除监听器
    return () => {
      unregisterMenuChangeListener(handleMenuChange);
    };
  }, []);

  // 根据当前路径初始化激活状态
  const initializeActiveState = (items) => {
    if (!items || items.length === 0) return;

    // 查找匹配当前路径的菜单项
    let foundActiveItem = false;

    // 从URL中获取view_id参数
    const urlParams = new URLSearchParams(location.search);
    const viewIdFromUrl = urlParams.get('view_id');

    // 首先检查子菜单项
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        for (const child of item.children) {
          // 优先检查URL中的view_id参数
          if (viewIdFromUrl && child.viewId === viewIdFromUrl) {
            setActiveItemId(null); // 不激活父菜单
            setActiveSubItemId(child.id);
            setExpandedItems((prev) => ({ 
              ...prev, 
              [item.id]: prev[item.id] !== undefined ? prev[item.id] : true 
            }));
            foundActiveItem = true;
            break;
          }
          // 如果location.state中有viewId，检查是否匹配
          else if (
            location.state?.viewId &&
            child.viewId === location.state.viewId
          ) {
            setActiveItemId(null); // 不激活父菜单
            setActiveSubItemId(child.id);
            setExpandedItems((prev) => ({ 
              ...prev, 
              [item.id]: prev[item.id] !== undefined ? prev[item.id] : true 
            }));
            foundActiveItem = true;
            break;
          }
          // 检查路径是否匹配（只有在没有view_id时才匹配默认菜单项）
          else if (!viewIdFromUrl && location.pathname === child.path) {
            setActiveItemId(null); // 不激活父菜单
            setActiveSubItemId(child.id);
            setExpandedItems((prev) => ({ 
              ...prev, 
              [item.id]: prev[item.id] !== undefined ? prev[item.id] : true 
            }));
            foundActiveItem = true;
            break;
          }
        }
        if (foundActiveItem) break;
      }
    }

    // 如果没有找到匹配的子菜单项，检查一级菜单项（只有在没有view_id时才匹配）
    if (!foundActiveItem && !viewIdFromUrl) {
      for (const item of items) {
        // 对于/explore路径，特殊处理
        if (
          (item.path === '/explore' && location.pathname === '/') ||
          location.pathname === item.path
        ) {
          setActiveItemId(item.id);
          setActiveSubItemId(null);
          foundActiveItem = true;
          break;
        }
        // 对于workspace路径，特殊处理
        else if (item.isWorkspace && location.pathname.startsWith('/workspace/')) {
          setActiveItemId(item.id);
          setActiveSubItemId(null);
          foundActiveItem = true;
          break;
        }
      }
    }

    // 如果仍未找到匹配项，使用默认逻辑（只有在没有view_id时才使用）
    if (!foundActiveItem && !viewIdFromUrl) {
      // 检查是否为特殊路径，这些路径不应该激活任何菜单项
      const specialPaths = ['/personal-info/'];
      const isSpecialPath = specialPaths.some((path) =>
        location.pathname.includes(path)
      );

      if (!isSpecialPath) {
        // 只有在非特殊路径下才默认激活第一个菜单项
        if (items.length > 0) {
          setActiveItemId(items[0].id);
          setActiveSubItemId(null);
        }
      } else {
        // 特殊路径下不激活任何菜单项
        setActiveItemId(null);
        setActiveSubItemId(null);
      }
    }

    // 如果有view_id但没有找到匹配的视图，清除激活状态
    if (!foundActiveItem && viewIdFromUrl) {
      setActiveItemId(null);
      setActiveSubItemId(null);
    }
  };

  useEffect(() => {
    const fetchCurrentWorkspace = async () => {
      try {
        setWorkspaceLoading(true);
      } catch (error) {
        console.error('获取当前工作区失败:', error);
      } finally {
        setWorkspaceLoading(false);
      }
    };

    fetchCurrentWorkspace();
  }, []);

  // 监听路径变化，重新初始化激活状态
  useEffect(() => {
    if (menuItems.length > 0) {

      initializeActiveState(menuItems);
    }
  }, [location.pathname, location.search, location.state, menuItems]);

  // 监听激活状态变化，用于调试
  useEffect(() => {

  }, [activeItemId, activeSubItemId, expandedItems]);

  useEffect(() => {
    if (location.pathname === '/tasks' && location.state?.activateTasksMenu) {
      menuItems.forEach((item) => {
        if (item.path === '/tasks') {
          setActiveItemId(item.id);
          setActiveSubItemId(null);
        }
      });
      return;
    }

    if (
      (location.pathname === '/explore' || location.pathname === '/tasks') &&
      location.state?.viewId
    ) {
      menuItems.forEach((item) => {
        if (
          item.children &&
          item.children.some((child) => child.viewId === location.state.viewId)
        ) {
          setExpandedItems((prev) => ({
            ...prev,
            [item.id]: prev[item.id] !== undefined ? prev[item.id] : true,
          }));

          setActiveItemId(null);

          const activeChild = item.children.find(
            (child) => child.viewId === location.state.viewId
          );
          if (activeChild) {
            setActiveSubItemId(activeChild.id);
          }
        }
      });
    }
  }, [location.pathname, location.state, menuItems]);

  // 监听来自首页的搜索激活请求
  useEffect(() => {
    // 检查是否需要激活搜索模式（从location.state中）
    if (location.state?.activateSearch && !isCollapsed) {
      setIsSearchMode(true);
      // 清除状态，避免重复激活
      window.history.replaceState({}, document.title);
    }
  }, [location.state, isCollapsed]);

  useEffect(() => {
    const handleStorage = () => {
      try {
        // 优先从初始化数据中获取 workspace 信息
        const initDataStr = localStorage.getItem('syntrust_init_data');
        if (initDataStr) {
          const initData = JSON.parse(initDataStr);
          if (initData.userDetail?.workspace) {
            setCurrentWorkspace(initData.userDetail.workspace);
            return;
          }
        }
        
        // 如果初始化数据中没有，尝试从用户数据中获取
        const userStr = localStorage.getItem('syntrust_user');
        if (userStr) {
          const userObj = JSON.parse(userStr);
          setCurrentWorkspace(userObj.workspace || null);
        }
      } catch {}
    };
    window.addEventListener('storage', handleStorage);
    // 兼容同页面内变更
    const interval = setInterval(handleStorage, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'SearchOutlined':
        return <SearchOutlined />;
      case 'FileOutlined':
        return <FileOutlined />;
      case 'AppstoreOutlined':
        return <AppstoreOutlined />;
      case 'TeamOutlined':
        return <TeamOutlined />;
      case 'ExploreIcon':
        return <ExploreIcon />;
      case 'TaskIcon':
        return <TaskIcon />;
      case 'ClockCircleOutlined':
        return <ClockCircleOutlined />;
      case 'FileTextOutlined':
        return <FileTextOutlined />;
      default:
        return null;
    }
  };

  // 切换菜单项的展开/折叠状态
  const toggleExpand = (e, itemId) => {
    e.stopPropagation(); // 阻止事件冒泡，不触发菜单项点击
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !(prev[itemId] !== undefined ? prev[itemId] : true),
    }));
  };

  // 切换最近打开的展开/折叠状态
  const toggleRecentlyOpenedExpanded = (e) => {
    e.stopPropagation(); // 阻止事件冒泡
    setRecentlyOpenedExpanded(!recentlyOpenedExpanded);
  };



  // 处理菜单项点击事件
  const handleMenuItemClick = (e, item) => {
    e.preventDefault();

    // 检查是否登录
    const token = getToken();
    if (!token) {
      // 允许未登录访问首页/探索页
      const isPublicPath = item.path === '/' || item.path === '/explore';
      
      if (!isPublicPath) {
        console.log('� 未登录，拦截跳转:', item.path);
        showLoginModal();
        return;
      }
    }

    // 如果点击的是子菜单项（视图），只设置子菜单项为激活状态，不激活父菜单
    if (item.viewId) {
      // 找到父菜单项，但只用于展开状态，不激活父菜单
      for (const menuItem of menuItems) {
        if (menuItem.children && menuItem.children.some(child => child.id === item.id)) {
          // 只设置子菜单项为激活状态，不激活父菜单
          setActiveItemId(null);
          setActiveSubItemId(item.id);
          // 确保父菜单展开，但允许用户手动收起
          setExpandedItems((prev) => ({ 
            ...prev, 
            [menuItem.id]: prev[menuItem.id] !== undefined ? prev[menuItem.id] : true 
          }));
          break;
        }
      }
    } else {
      // 如果点击的是主菜单项，设置主菜单项为激活状态并清除子菜单激活状态
      setActiveItemId(item.id);
      setActiveSubItemId(null);
    }

    // 特殊处理"找队友"菜单，跳转时带上默认筛选参数
    if (item.path === '/explore/friends') {
      const filterParams = item.defaultFilter || {
        exprs: [{ field: 'tags', op: 'EQ', values: ['找队友'] }],
      };
      navigate(item.path, {
        state: {
          filterParams,
          clearFilters: false,
        },
      });
      return;
    }
    // 特殊处理"找点子"菜单，跳转时带上默认筛选参数
    if (item.path === '/explore/ideas') {
      const filterParams = item.defaultFilter || {
        exprs: [{ field: 'tags', op: 'NOT_IN', values: ['找队友'] }],
      };
      navigate(item.path, {
        state: {
          filterParams,
          clearFilters: false,
        },
      });
      return;
    }

    // 特殊处理工作台菜单，动态获取workspace ID
    if (item.isWorkspace) {
      const workspaceId = getDefaultTeamId();
      // 如果没有团队，使用 'current' 作为默认值
      const finalWorkspaceId = workspaceId || 'current';
      navigate(`/workspace/${finalWorkspaceId}`);
      return;
    }

    // 处理视图菜单项
    if (item.isView && item.viewId) {
      // 获取视图数据
      const viewData = getSavedViewData(item.viewId);

      if (viewData) {
        // 深拷贝视图数据以确保不会有引用问题
        const viewDataCopy = JSON.parse(JSON.stringify(viewData));

        // 验证视图数据中的筛选条件
        if (!viewDataCopy.filterParams) {
          viewDataCopy.filterParams = null;
        } else if (Array.isArray(viewDataCopy.filterParams)) {
          const validFilters = viewDataCopy.filterParams.filter(
            (filter) =>
              filter &&
              filter.exprs &&
              Array.isArray(filter.exprs) &&
              filter.exprs.length > 0
          );
          viewDataCopy.filterParams =
            validFilters.length > 0 ? validFilters : null;
        } else if (viewDataCopy.filterParams.exprs) {
          if (
            !Array.isArray(viewDataCopy.filterParams.exprs) ||
            viewDataCopy.filterParams.exprs.length === 0
          ) {
            viewDataCopy.filterParams = null;
          }
        } else {
          viewDataCopy.filterParams = null;
        }

        if (
          !viewDataCopy.sortParams ||
          typeof viewDataCopy.sortParams !== 'object' ||
          !viewDataCopy.sortParams.field
        ) {
          viewDataCopy.sortParams = null;
        }

        localStorage.setItem('current_view_data', JSON.stringify(viewDataCopy));

        navigate(item.path, {
          state: {
            viewId: item.viewId,
            applyViewFilters: true,
            filterParams: viewDataCopy.filterParams,
            sortParams: viewDataCopy.sortParams,
            groupParams: viewDataCopy.groupParams,
          },
        });
        return;
      }
    }

    // 对于普通菜单项（一级菜单），清空筛选条件
    navigate(item.path, {
      state: {
        clearFilters: true, // 添加标志，指示页面组件清空筛选条件
      },
    });
  };

  // 处理最近打开的项目点击
  const handleRecentItemClick = (e, item) => {
    e.preventDefault();
    
    // 检查是否登录
    const token = getToken();
    if (!token) {
      // 允许未登录访问首页/探索页
      const isPublicPath = item.path === '/' || item.path === '/explore';
      
      if (!isPublicPath) {
        console.log('🔒 未登录，拦截最近打开跳转:', item.path);
        showLoginModal();
        return;
      }
    }

    // 导航到对应的路径
    navigate(item.path);
  };

  // 从最近打开列表中移除项目
  const removeRecentItem = (e, itemId) => {
    e.stopPropagation();
    const updated = removeRecentFromStorage(itemId);
    setRecentlyOpenedItems(updated);
  };

  // 处理菜单项重命名
  const handleRenameClick = (e, item, parentId) => {
    // 在Ant Design的Dropdown菜单项点击事件中，e是{key, keyPath, domEvent}
    // 如果e有domEvent属性，则使用它，否则直接使用e
    if (e && e.domEvent && e.domEvent.stopPropagation) {
      e.domEvent.stopPropagation();
    } else if (e && e.stopPropagation) {
      e.stopPropagation(); // 原始事件对象直接使用
    }

    setCurrentRenameItem({ ...item, parentId });
    setNewMenuName(item.title);
    setRenameModalVisible(true);
  };

  // 处理重命名确认
  const handleRenameConfirm = () => {
    if (!newMenuName.trim()) {
      window.message?.error('菜单名称不能为空');
      return;
    }

    // 更新菜单数据
    const updatedMenuItems = menuItems.map((item) => {
      if (item.id === currentRenameItem.parentId) {
        return {
          ...item,
          children: item.children.map((child) =>
            child.id === currentRenameItem.id
              ? { ...child, title: newMenuName }
              : child
          ),
        };
      }
      return item;
    });

    setMenuItems(updatedMenuItems);

    // 保存更新后的菜单数据
    saveMenuData(updatedMenuItems);

    window.message?.success('重命名成功');
    setRenameModalVisible(false);
  };

  // 处理重命名取消
  const handleRenameCancel = () => {
    setRenameModalVisible(false);
  };

  // 处理菜单项删除点击
  const handleDeleteClick = (e, item, parentId) => {
    // 在Ant Design的Dropdown菜单项点击事件中，e是{key, keyPath, domEvent}
    // 如果e有domEvent属性，则使用它，否则直接使用e
    if (e && e.domEvent && e.domEvent.stopPropagation) {
      e.domEvent.stopPropagation();
    } else if (e && e.stopPropagation) {
      e.stopPropagation(); // 原始事件对象直接使用
    }

    setItemToDelete({ ...item, parentId });
    setDeleteModalVisible(true);
  };

  // 处理删除确认
  const handleDeleteConfirm = () => {
    if (!itemToDelete) return;

    // 更新菜单数据，过滤掉要删除的子菜单项
    const updatedMenuItems = menuItems.map((item) => {
      if (item.id === itemToDelete.parentId) {
        return {
          ...item,
          children: item.children.filter(
            (child) => child.id !== itemToDelete.id
          ),
        };
      }
      return item;
    });

    setMenuItems(updatedMenuItems);

    // 保存更新后的菜单数据
    saveMenuData(updatedMenuItems);

    window.message?.success('删除成功');
    setDeleteModalVisible(false);

    // 如果删除的是当前激活的子菜单项，清除激活状态
    if (activeSubItemId === itemToDelete.id) {
      setActiveSubItemId(null);
    }
  };

  // 处理删除取消
  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
  };

  // 渲染子菜单项
  const renderSubMenuItems = (children, parentId) => {
    if (!children || children.length === 0) return null;

    return (
      <div className="pl-7 pr-2 mt-1">
        {children.map((child) => (
          <div
            key={child.id}
            className={`flex items-center justify-between px-3 py-2 rounded-full cursor-pointer mb-0.5 h-8 w-[180px] transition-all ${
              activeSubItemId === child.id
                ? 'bg-white border border-black'
                : 'hover:bg-[#f0f7ff]'
            }`}
          >
            <div
              className="flex items-center flex-1"
              onClick={(e) => handleMenuItemClick(e, child)}
            >
              <span className="flex items-center justify-center text-sm text-[#595959] mr-2">
                {child.icon && getIconComponent(child.icon)}
              </span>
              <span className="text-xs text-black">{child.title}</span>
            </div>

            {!child.disableRename && !child.disableDelete && (
              <Dropdown
                menu={{
                  items: [
                    ...(!child.disableRename
                      ? [
                          {
                            key: 'rename',
                            icon: <EditOutlined />,
                            label: '重命名',
                            onClick: (info) =>
                              handleRenameClick(info, child, parentId),
                          },
                        ]
                      : []),
                    ...(!child.disableDelete
                      ? [
                          {
                            key: 'delete',
                            icon: <DeleteOutlined />,
                            label: '删除',
                            onClick: (info) =>
                              handleDeleteClick(info, child, parentId),
                            danger: true,
                          },
                        ]
                      : []),
                  ],
                }}
                trigger={['click']}
                placement="bottomRight"
              >
                <button
                  className="bg-transparent border-none cursor-pointer p-0.5 rounded text-[#8c8c8c] text-sm flex items-center justify-center opacity-0 transition-opacity hover:bg-[#f0f0f0] hover:text-[#1890ff] group-hover:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <EllipsisOutlined />
                </button>
              </Dropdown>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderMenuItem = (item) => {
    // 检查是否有子菜单
    const hasChildren = item.children && item.children.length > 0;

    // 检查是否激活 - 一级菜单只有在自身被激活且没有激活的子菜单时才激活
    const isActive = activeItemId === item.id && !activeSubItemId;

    // 检查是否应该展开子菜单 - 优先使用手动展开状态，如果有激活的子菜单且没有手动收起则展开
    const isExpanded =
      expandedItems[item.id] !== undefined
        ? expandedItems[item.id]
        : (hasChildren &&
            item.children.some((child) => activeSubItemId === child.id));

    // 资产菜单特殊处理
    // if (item.title === '资产') {
    //   return (
    //     <Tooltip title="活动开启后开放，敬请期待" placement="right">
    //       <div
    //         key={item.id}
    //         className={`flex items-center justify-between transition-colors relative rounded-full mb-0.5 group select-none opacity-60 cursor-not-allowed ${
    //           isCollapsed
    //             ? 'h-10 w-10 mx-auto justify-center'
    //             : 'h-10 w-[200px] mx-[14px] px-4 py-2'
    //         }`}
    //       >
    //         <div
    //           className={`flex items-center ${
    //             isCollapsed ? 'justify-center mx-auto' : 'gap-3'
    //           }`}
    //         >
    //           <span className="flex items-center justify-center text-base text-black">
    //             {item.icon && getIconComponent(item.icon)}
    //           </span>
    //           {!isCollapsed && (
    //             <span className="text-sm text-black">{item.title}</span>
    //           )}
    //         </div>
    //       </div>
    //     </Tooltip>
    //   );
    // }

    return (
      <div key={item.id} className="mb-0.5 group">
        <div
          className={`flex items-center justify-between cursor-pointer transition-colors relative rounded-full ${
            isActive
              ? 'bg-white border border-black'
              : 'hover:bg-[#f0f7ff] text-black'
          } ${
            isCollapsed
              ? 'h-10 w-10 mx-auto justify-center'
              : 'h-10 w-[200px] mx-[14px] px-4 py-2'
          }`}
          onClick={(e) => handleMenuItemClick(e, item)}
        >
          <div
            className={`flex items-center ${
              isCollapsed ? 'justify-center mx-auto' : 'gap-3'
            }`}
          >
            <span className="flex items-center justify-center text-base text-black">
              {item.icon && getIconComponent(item.icon)}
            </span>
            {!isCollapsed && (
              <span className="text-sm text-black">{item.title}</span>
            )}
          </div>

          {/* 对有子菜单的项添加展开/折叠图标，仅在非折叠状态下显示 */}
          {!isCollapsed && hasChildren && (
            <span
              className="text-xs text-[#8c8c8c] p-1 rounded transition-all ml-1 hover:bg-[rgba(0,0,0,0.04)] hover:text-[#1890ff]"
              onClick={(e) => toggleExpand(e, item.id)}
            >
              {isExpanded ? <MinusOutlined /> : <PlusOutlined />}
            </span>
          )}
        </div>

        {/* 仅当不折叠且有子菜单且手动展开或有激活的子项时渲染子菜单 */}
        {!isCollapsed &&
          hasChildren &&
          isExpanded &&
          renderSubMenuItems(item.children, item.id)}
      </div>
    );
  };

  const renderAd = () => {
    if (isCollapsed) return null;
    const token = getToken();
    const user_id = getCurrentUser().id;
    const eventUrl = getEventUrl();
    
    return (
      <div className="px-[14px] py-2 bg-white border-t border-gray-200">
        {/* 广告图片 */}
        <img
          src={adImage}
          alt="Advertisement"
          className="w-[200px] h-auto rounded-lg object-cover"
          onClick={() => {
            window.open(`${eventUrl}/?token=Bearer ${token}&userId=${user_id}`, '_blank');
          }}
        />
      </div>
    );
  };

  // 渲染最近打开的项目
  const renderRecentlyOpenedItems = () => {
    if (isCollapsed) return null;

    return (
      <div className="bg-white border-t border-gray-200">
        {/* 最近打开标题栏 */}
        <div
          className="flex items-center justify-between px-3 py-2 w-[200px] hover:bg-[#f0f7ff] rounded-full cursor-pointer mx-[14px] text-black"
          onClick={toggleRecentlyOpenedExpanded}
        >
          <div className="flex items-center gap-2">
            <span className="">
              <ClockCircleOutlined />
            </span>
            <span className="font-medium">最近打开</span>
          </div>
          <span>
            {recentlyOpenedExpanded ? <MinusOutlined /> : <PlusOutlined />}
          </span>
        </div>

        {/* 分割线 */}
        <div className="mx-4 my-2">
          <div className="w-[196px] h-[0.5px] bg-gray-300"></div>
        </div>

        {/* 最近打开的项目列表 */}
        {recentlyOpenedExpanded && (
          <div className="mx-[14px] max-h-40 overflow-y-auto overflow-x-hidden hide-scrollbar">
            {recentlyOpenedItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between px-3 py-2 w-[200px] hover:bg-[#f0f7ff] rounded-full cursor-pointer"
                onClick={(e) => handleRecentItemClick(e, item)}
              >
                {/* Limit text to one line with ellipsis */}
                <div className="flex items-center w-[130px] gap-2 overflow-hidden">
                  <span className="flex items-center justify-center">
                    {getIconComponent(item.icon)}
                  </span>
                  <span className="text-sm truncate">{item.title}</span>
                </div>
                <button
                  className="opacity-50 hover:opacity-100 bg-transparent border-none cursor-pointer"
                  onClick={(e) => removeRecentItem(e, item.id)}
                >
                  <CloseOutlined />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // workspace pop内容
  const workspacePopContent = (
    <div className={styles.popover}>
      {/* 顶部用户与workspace信息 */}
      <div className={styles.header}>
        <Avatar src={userAvatar} size={40} className={styles.avatar}>
          {workspaceName?.[0] || 'A'}
        </Avatar>
        <div className={styles.info}>
          <span className={styles.name}>{workspaceName}</span>
          <span className={styles.email}>{userEmail}</span>
        </div>
      </div>
      {/* 分割线 */}
      <div className={styles.divider} />
      {/* 操作区 */}
      <div className={styles.actions}>
        {/* 普通workspace+认证 */}
        <div className={styles.actionRowNoHover}>
          {/* 信用卡icon */}
          <span className={styles.icon}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect
                x="2"
                y="4"
                width="14"
                height="10"
                rx="2"
                fill="#fff"
                stroke="#05042E"
                strokeWidth="1.5"
              />
              <rect x="2" y="7" width="14" height="2" fill="#D9D9D9" />
            </svg>
          </span>
          <span className={styles.actionText}>普通workspace</span>
          <button
            className={styles.authBtn}
            onClick={() => {
              setShowWorkspacePop(false);
              setShowAuthModal(true);
            }}
          >
            认证
          </button>
        </div>
        {/* workspace主页 */}
        <div
          className={styles.actionRow}
          onClick={() => {
            setShowWorkspacePop(false);
            const workspaceId = getDefaultTeamId();
            navigate(`/workspace/${workspaceId}`);
          }}
        >
          {/* 人形icon */}
          <span className={styles.icon}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle
                cx="9"
                cy="6.5"
                r="3.5"
                fill="#fff"
                stroke="#05042E"
                strokeWidth="1.5"
              />
              <rect
                x="3"
                y="12"
                width="12"
                height="3"
                rx="1.5"
                fill="#fff"
                stroke="#000"
                strokeWidth="0.5"
              />
            </svg>
          </span>
          <span className={styles.actionText}>workspace主页</span>
        </div>
      </div>
    </div>
  );

  // 修改renderWorkspaceArea，替换下拉icon为Popover
  const renderWorkspaceArea = () => {
    if (isCollapsed) {
      return null;
    }
    return (
      <div className="flex items-center px-4 pb-1 h-[30px]">
        <div className="flex items-center justify-center w-4 h-4 bg-[#1a1a1a] rounded-md text-white font-bold text-[10px] mr-3">
          A
        </div>
        <div className="font-semibold text-xs">
          {workspaceLoading
            ? '加载中...'
            : currentWorkspace
            ? currentWorkspace.name
            : 'Alibaba'}
        </div>
        <Popover
          open={showWorkspacePop}
          onOpenChange={setShowWorkspacePop}
          trigger="click"
          placement="bottomRight"
          content={workspacePopContent}
          overlayStyle={{ padding: 0 }}
        >
          <Button
            className="ml-auto"
            type="text"
            icon={<DownOutlined />}
            size="small"
            style={{ border: 'none', boxShadow: 'none' }}
          />
        </Popover>
      </div>
    );
  };

  // 监听最近打开列表的变化
  useEffect(() => {
    const handleStorageChange = () => {
      loadRecentlyOpenedItems();
    };

    const handleRecentlyOpenedUpdated = () => {
      loadRecentlyOpenedItems();
    };

    // 监听跨标签页的 storage 事件
    window.addEventListener('storage', handleStorageChange);
    // 监听同页面内的自定义事件
    window.addEventListener(
      RECENTLY_OPENED_UPDATED_EVENT,
      handleRecentlyOpenedUpdated
    );

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(
        RECENTLY_OPENED_UPDATED_EVENT,
        handleRecentlyOpenedUpdated
      );
    };
  }, [menuItems]);

  const sidebarInner = (
    <>
      <div className="">
        <div
          className={`pt-4 ${isCollapsed ? 'space-y-3 px-2' : 'space-y-6 px-4'}`}
          aria-label="主导航"
        >
          {loading ? (
            <div className="p-4 text-center">加载中...</div>
          ) : (
            menuItems.map((item) => {
              const isActive = activeItemId === item.id && !activeSubItemId;
              const icon = item.icon && getIconComponent(item.icon);

              if (isActive) {
                if (isCollapsed) {
                  return (
                    <button
                      key={item.id}
                      className="w-full flex flex-col items-center py-2 transition-all duration-300 ease-in-out"
                      onClick={(e) => handleMenuItemClick(e, item)}
                      aria-current="page"
                      title={item.title}
                    >
                      <span className="h-9 w-9 rounded-full bg-[#C1FF00] text-[#222] flex items-center justify-center">
                        {icon}
                      </span>
                      <span className="mt-1 text-xs font-bold text-white leading-none">
                        {item.title}
                      </span>
                    </button>
                  );
                }

                return (
                  <button
                    key={item.id}
                    className="h-9 rounded-full bg-[#C1FF00] text-black flex items-center transition-all duration-300 ease-in-out w-full pr-3"
                    onClick={(e) => handleMenuItemClick(e, item)}
                    aria-current="page"
                    title=""
                  >
                    <span className="h-9 w-9 flex items-center justify-center mr-3">
                      {icon}
                    </span>
                    <span className="text-sm font-bold">{item.title}</span>
                  </button>
                );
              }

              if (isCollapsed) {
                return (
                  <button
                    key={item.id}
                    className="w-full flex flex-col items-center py-2 transition-all duration-300 ease-in-out group"
                    onClick={(e) => handleMenuItemClick(e, item)}
                    title={item.title}
                  >
                    <span className="h-9 w-9 rounded-full bg-[#D9D9D9] text-[#222] flex items-center justify-center transition-colors group-hover:bg-[#E5E5E5]">
                      {icon}
                    </span>
                    <span className="mt-1 text-xs font-bold text-white group-hover:text-white/80 transition-colors leading-none">
                      {item.title}
                    </span>
                  </button>
                );
              }

              return (
                <button
                  key={item.id}
                  className="h-9 flex items-center transition-all duration-300 ease-in-out group w-full"
                  onClick={(e) => handleMenuItemClick(e, item)}
                  title=""
                >
                  <span className="h-9 w-9 rounded-full bg-[#D9D9D9] text-[#222] flex items-center justify-center transition-colors group-hover:bg-[#E5E5E5] mr-3">
                    {icon}
                  </span>
                  <span className="text-sm font-bold text-white group-hover:text-white/80 transition-colors">
                    {item.title}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
      <div
        className={`${
          variant === 'drawer' ? 'mt-auto' : 'fixed bottom-0'
        } ${isCollapsed ? 'w-[72px]' : 'w-[168px]'}`}
      >
        <div className="mt-auto px-3 pb-3">
          <UserInfoArea isCollapsed={isCollapsed} />
        </div>
      </div>
    </>
  );

  return (
    <>
      {variant === 'drawer' ? (
        <div
          className="bg-[#222222] text-white transition-all duration-300 flex flex-col"
          style={{ width: siderWidth, height: '100%' }}
        >
          {sidebarInner}
        </div>
      ) : (
        <Sider
          className={`bg-[#222222] text-white transition-all duration-300 ${
            isCollapsed ? 'w-[72px]' : 'w-[168px]'
          }`}
          width={siderWidth}
          collapsible
          collapsed={isCollapsed}
          trigger={null}
          collapsedWidth={72}
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#222222',
            borderRight: 'none',
          }}
        >
          {sidebarInner}
        </Sider>
      )}
      {/* 重命名菜单项的Modal */}
      <Modal
        title="重命名菜单"
        open={renameModalVisible}
        onOk={handleRenameConfirm}
        onCancel={handleRenameCancel}
        okText="确认"
        cancelText="取消"
      >
        <Input
          placeholder="请输入新的菜单名称"
          value={newMenuName}
          onChange={(e) => setNewMenuName(e.target.value)}
          style={{ marginTop: '16px' }}
        />
      </Modal>
      {/* 删除菜单项的确认Modal */}
      <Modal
        title="删除菜单"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="确认删除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>确定要删除菜单"{itemToDelete?.title}"吗？此操作不可撤销。</p>
      </Modal>
      {/* 工作区认证状态Modal */}
      <WorkspaceAuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        members={mockMembers}
      />
    </>
  );
};

export default AppSidebar;
